import React from 'react';
import { Box, Typography, Button, CircularProgress, Alert } from '@mui/material';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import StepIndicator from './StepIndicator';
import FieldsStep from './FieldsStep';
import SelectionStep from './SelectionStep';
import { Fade } from '@mui/material';

/**
 * FormCard
 * --------
 * Renders the step indicator, scrollable form content, and sticky/fixed nav footer.
 *
 * variant:
 *   'card'   — wrapped in a white rounded card (default, used by image-top / image-bg)
 *   'flush'  — no card shell; form fills its parent column, nav footer sticks to the
 *              bottom of the viewport column (used by image-left / image-right)
 *   'incard' — same scrollable+sticky behaviour as 'flush' but rendered inside a
 *              parent card shell (used by card-left / card-right)
 */
const FormCard = ({
  steps = [],
  activeStep,
  currentStep,
  isLastStep,
  fieldData,
  selectionData,
  fieldErrors,
  selectionErrors,
  error,
  submitting,
  onFieldChange,
  onSelectionChange,
  onNext,
  onBack,
  onSubmit,
  onClearError,
  submitLabel = 'Finish Registration',
  variant = 'card',
}) => {

  // ── Shared: step indicator ──────────────────────────────────────────────────
  const stepIndicator = steps.length > 1 && (
    <Box sx={{ px: { xs: 3, md: 5 }, pt: 4, pb: 0, flexShrink: 0 }}>
      <StepIndicator steps={steps} activeStep={activeStep} />
    </Box>
  );

  // ── Shared: error banner ────────────────────────────────────────────────────
  const errorBanner = error && (
    <Box sx={{ px: { xs: 3, md: 5 }, py: 2, flexShrink: 0 }}>
      <Alert severity="error" onClose={onClearError}>{error}</Alert>
    </Box>
  );

  // ── Shared: scrollable step content ────────────────────────────────────────
  const stepContent = (
    <Box sx={{ px: { xs: 3, md: 5 }, pt: 3, pb: 2, flex: 1, overflowY: 'auto' }}>
      <Fade in key={activeStep} timeout={350}>
        <Box>
          {currentStep?.type === 'fields' && (
            <FieldsStep
              step={currentStep}
              fieldData={fieldData}
              onChange={onFieldChange}
              errors={fieldErrors}
            />
          )}
          {currentStep?.type === 'selection' && (
            <SelectionStep
              step={currentStep}
              selectionData={selectionData}
              onChange={onSelectionChange}
              errors={selectionErrors}
            />
          )}
        </Box>
      </Fade>
    </Box>
  );

  // ── Shared: navigation footer ───────────────────────────────────────────────
  const navFooter = (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        px: { xs: 3, md: 5 },
        py: 3,
        flexShrink: 0,
        borderTop: '1px solid',
        borderColor: 'divider',
        bgcolor: 'background.paper',
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <InfoOutlinedIcon sx={{ fontSize: 16, color: 'text.disabled' }} />
        <Typography variant="caption" color="text.disabled">Your data is secured</Typography>
      </Box>
      <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
        {activeStep > 0 && (
          <Button startIcon={<ArrowBackIcon />} onClick={onBack} sx={{ color: 'text.secondary' }}>
            Back to {steps[activeStep - 1]?.label}
          </Button>
        )}
        {isLastStep ? (
          <Button
            variant="contained"
            size="large"
            onClick={onSubmit}
            disabled={submitting}
            sx={{ minWidth: 180 }}
          >
            {submitting ? <CircularProgress size={22} color="inherit" /> : submitLabel}
          </Button>
        ) : (
          <Button variant="contained" size="large" onClick={onNext} sx={{ minWidth: 120 }}>
            Next
          </Button>
        )}
      </Box>
    </Box>
  );

  // ── variant: 'card' ─────────────────────────────────────────────────────────
  // White rounded card. Step indicator and nav are not scrollable — only content scrolls.
  if (variant === 'card') {
    return (
      <Box
        sx={{
          width: '100%',
          backgroundColor: 'white',
          borderRadius: 4,
          border: '1px solid',
          borderColor: 'divider',
          boxShadow: '0 2px 24px rgba(0,0,0,0.08)',
          overflow: 'hidden',
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        {stepIndicator}
        {/* For card variant keep original non-scroll behaviour — form just expands */}
        <Box sx={{ px: { xs: 3, md: 5 }, pt: 3, pb: 0 }}>
          <Fade in key={activeStep} timeout={350}>
            <Box>
              {currentStep?.type === 'fields' && (
                <FieldsStep step={currentStep} fieldData={fieldData} onChange={onFieldChange} errors={fieldErrors} />
              )}
              {currentStep?.type === 'selection' && (
                <SelectionStep step={currentStep} selectionData={selectionData} onChange={onSelectionChange} errors={selectionErrors} />
              )}
            </Box>
          </Fade>
        </Box>
        {errorBanner}
        <Box
          sx={{
            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
            px: { xs: 3, md: 5 }, py: 3, mt: 1,
            borderTop: '1px solid', borderColor: 'divider',
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <InfoOutlinedIcon sx={{ fontSize: 16, color: 'text.disabled' }} />
            <Typography variant="caption" color="text.disabled">Your data is secured</Typography>
          </Box>
          <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
            {activeStep > 0 && (
              <Button startIcon={<ArrowBackIcon />} onClick={onBack} sx={{ color: 'text.secondary' }}>
                Back to {steps[activeStep - 1]?.label}
              </Button>
            )}
            {isLastStep ? (
              <Button variant="contained" size="large" onClick={onSubmit} disabled={submitting} sx={{ minWidth: 180 }}>
                {submitting ? <CircularProgress size={22} color="inherit" /> : submitLabel}
              </Button>
            ) : (
              <Button variant="contained" size="large" onClick={onNext} sx={{ minWidth: 120 }}>Next</Button>
            )}
          </Box>
        </Box>
      </Box>
    );
  }

  // ── variant: 'flush' ────────────────────────────────────────────────────────
  // No card shell. The parent column is already a flex column that fills the viewport.
  // Step indicator is pinned at the top, content scrolls in the middle,
  // nav footer is pinned at the bottom via mt: 'auto' + flexShrink: 0.
  if (variant === 'flush') {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0, bgcolor: 'background.paper' }}>
        {stepIndicator}
        {stepContent}
        {errorBanner}
        {navFooter}
      </Box>
    );
  }

  // ── variant: 'incard' ───────────────────────────────────────────────────────
  // Used inside the card-left / card-right layouts. The parent card provides the
  // shell; this variant just contributes the inner scrollable + sticky footer.
  if (variant === 'incard') {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0, backgroundColor: 'white' }}>
        {stepIndicator}
        {stepContent}
        {errorBanner}
        {navFooter}
      </Box>
    );
  }

  return null;
};

export default FormCard;
